(() => {
  "use strict";

  const BUILDER_SELECTOR = "[data-fl-gift-hamper]";
  const DRAFT_VERSION = 1;
  const HAMPER_SCHEMA = "flss.gift-hamper.v1";
  const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

  const clamp = (value, minimum, maximum) => Math.min(maximum, Math.max(minimum, Number(value) || minimum));
  const clean = (value, limit = 240) => String(value ?? "").replace(/\s+/g, " ").trim().slice(0, limit);
  const nonNegativeInteger = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : fallback;
  };
  const positiveInteger = (value, fallback = 1) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
  };
  const escapeHtml = (value, limit = 4000) => clean(value, limit).replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;"
  }[character]));
  const moneyAmount = (value) => `R${(nonNegativeInteger(value) / 100).toFixed(2)}`;
  const money = (value) => `${moneyAmount(value)} excl. VAT`;
  const ajaxId = (value) => {
    const normalized = clean(value, 40);
    const numeric = Number(normalized);
    return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : normalized;
  };
  const uid = () => globalThis.crypto?.randomUUID?.()
    || `hamper-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

  function todayIso() {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Johannesburg",
      year: "numeric",
      month: "2-digit",
      day: "2-digit"
    }).formatToParts(new Date());
    const calendar = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    return `${calendar.year}-${calendar.month}-${calendar.day}`;
  }

  function validIsoDate(value) {
    const normalized = clean(value, 10);
    const match = normalized.match(ISO_DATE_PATTERN);
    if (!match) return "";
    const [, year, month, day] = match;
    const parsed = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return parsed.getUTCFullYear() === Number(year)
      && parsed.getUTCMonth() === Number(month) - 1
      && parsed.getUTCDate() === Number(day)
      ? normalized
      : "";
  }

  function requestedDateIsValid(value, minimum = todayIso()) {
    if (!clean(value, 10)) return true;
    const normalized = validIsoDate(value);
    return Boolean(normalized && normalized >= minimum);
  }

  function normalizeVariant(source = {}) {
    const variantId = clean(source?.variantId, 40);
    return {
      variantId,
      priceCents: nonNegativeInteger(source?.priceCents),
      available: source?.available === true && Boolean(variantId)
    };
  }

  function normalizeConfig(source = {}) {
    const vouchers = Object.fromEntries(Object.entries(source?.vouchers || {}).map(([value, variant]) => [clean(value, 20), normalizeVariant(variant)]));
    return {
      product: {
        id: clean(source?.product?.id, 40),
        title: clean(source?.product?.title, 160),
        M: normalizeVariant(source?.product?.M),
        L: normalizeVariant(source?.product?.L)
      },
      wrapping: {
        M: normalizeVariant(source?.wrapping?.M),
        L: normalizeVariant(source?.wrapping?.L)
      },
      vouchers,
      cartAddUrl: clean(source?.cartAddUrl, 500),
      cartUrl: clean(source?.cartUrl, 500) || "/cart",
      messageEndpoint: clean(source?.messageEndpoint, 500),
      messageLimit: clamp(source?.messageLimit || 180, 100, 180),
      minItems: clamp(source?.minItems || 2, 1, 12),
      minMerchandiseCents: nonNegativeInteger(source?.minMerchandiseCents, 25000),
      mediumWeightGrams: nonNegativeInteger(source?.mediumWeightGrams, 2000),
      mediumItems: clamp(source?.mediumItems || 8, 1, 12),
      maxWeightGrams: nonNegativeInteger(source?.maxWeightGrams, 6000),
      maxItems: clamp(source?.maxItems || 12, 1, 24)
    };
  }

  function compactManifest(items = []) {
    return items.map((entry) => ({
      variantId: clean(entry?.variantId, 40),
      sku: clean(entry?.sku, 80),
      name: clean(entry?.name, 180),
      size: clean(entry?.size, 20),
      quantity: positiveInteger(entry?.quantity),
      weightGrams: nonNegativeInteger(entry?.weightGrams),
      role: "product"
    }));
  }

  function calculateTotals(metrics = {}, packaging = null, wrapping = null, voucher = null) {
    const merchandiseCents = nonNegativeInteger(metrics?.merchandiseCents);
    const packagingCents = positiveInteger(metrics?.itemCount, 0) ? nonNegativeInteger(packaging?.priceCents) : 0;
    const wrappingCents = nonNegativeInteger(wrapping?.priceCents);
    const voucherCents = nonNegativeInteger(voucher?.priceCents);
    return {
      merchandiseCents,
      packagingCents,
      wrappingCents,
      voucherCents,
      totalCents: merchandiseCents + packagingCents + wrappingCents + voucherCents
    };
  }

  function privateChildProperties({ id, groupId, role, carton }) {
    return {
      _flss_gift_hamper_id: clean(id, 120),
      _flss_gift_hamper_group_id: clean(groupId, 120),
      _flss_gift_hamper_schema: HAMPER_SCHEMA,
      _flss_gift_hamper_role: clean(role, 40),
      _flss_gift_hamper_carton: carton === "L" ? "L" : "M"
    };
  }

  class HamperBuilder {
    constructor(root) {
      this.root = root;
      this.products = [...root.querySelectorAll("[data-hamper-product]")];
      this.items = [];
      this.suggestions = [];
      this.adding = false;
      this.generating = false;
      this.config = this.readConfig();
      this.storageKey = `flss:gift-hamper:${DRAFT_VERSION}:${this.config.product.id || "product"}`;
      this.bind();
      this.prepareControls();
      this.restoreDraft();
      this.render();
    }

    query(selector) {
      return this.root.querySelector(selector);
    }

    readConfig() {
      const node = this.query("[data-fl-gift-hamper-config]");
      if (!node) throw new Error("Gift hamper configuration is missing.");
      let parsed;
      try {
        parsed = JSON.parse(node.textContent || "{}");
      } catch {
        throw new Error("Gift hamper configuration is invalid.");
      }
      return normalizeConfig(parsed);
    }

    prepareControls() {
      const date = this.query("[data-hamper-date]");
      if (date) date.min = todayIso();
      const suggestions = this.query("[data-hamper-suggestions]");
      if (suggestions) {
        suggestions.setAttribute("role", "status");
        suggestions.setAttribute("aria-live", "polite");
      }
      this.products.forEach((product) => {
        const label = clean(product.dataset.flavour, 120) || "product";
        const output = product.querySelector("[data-hamper-quantity]");
        if (output) {
          output.setAttribute("aria-live", "polite");
          output.setAttribute("aria-label", `Quantity of ${label}`);
        }
        product.querySelector("[data-hamper-minus]")?.setAttribute("aria-label", `Remove one ${label}`);
        product.querySelector("[data-hamper-plus]")?.setAttribute("aria-label", `Add one ${label}`);
      });
      this.syncVoucherAvailability();
    }

    bind() {
      this.root.addEventListener("change", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.matches("[data-hamper-size]")) return this.selectVariant(target);
        if (target.matches("[data-hamper-wrap], [data-hamper-voucher], [data-hamper-date], [data-hamper-recipient], [data-hamper-sender], [data-hamper-message]")) {
          this.notice("");
          this.render();
        }
      });
      this.root.addEventListener("input", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        if (target.matches("[data-hamper-date], [data-hamper-recipient], [data-hamper-sender], [data-hamper-message]")) {
          this.notice("");
          this.render();
        }
      });
      this.root.addEventListener("click", (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;
        const quantityButton = target.closest("[data-hamper-plus], [data-hamper-minus]");
        if (quantityButton) return this.adjustQuantity(quantityButton);
        if (target.closest("[data-hamper-clear]")) return this.startOver();
        if (target.closest("[data-hamper-add]")) return void this.addToCart();
        if (target.closest("[data-hamper-generate]")) return void this.generateMessages();
        const suggestion = target.closest("[data-hamper-suggestion]");
        if (suggestion) {
          const message = this.query("[data-hamper-message]");
          if (message) message.value = clean(suggestion.textContent, this.config.messageLimit);
          this.suggestions = [];
          this.notice("Message added. You can still edit it before checkout.", "success");
          this.render();
        }
      });
    }

    fallbackWeight(size) {
      return size === "1kg" ? 1000 : size === "500g" ? 500 : size === "375ml" ? 420 : 200;
    }

    itemFromOption(product, option, quantity = 1) {
      return {
        product,
        variantId: clean(option.value, 40),
        sku: clean(option.dataset.sku, 80),
        name: clean(option.dataset.name, 180),
        size: clean(option.dataset.size, 20),
        priceCents: nonNegativeInteger(option.dataset.priceCents),
        weightGrams: nonNegativeInteger(option.dataset.weight) || this.fallbackWeight(option.dataset.size),
        quantity: positiveInteger(quantity),
        image: clean(option.dataset.image, 1000)
      };
    }

    metrics(items = this.items) {
      return items.reduce((summary, entry) => ({
        itemCount: summary.itemCount + entry.quantity,
        weightGrams: summary.weightGrams + entry.quantity * entry.weightGrams,
        merchandiseCents: summary.merchandiseCents + entry.quantity * entry.priceCents
      }), { itemCount: 0, weightGrams: 0, merchandiseCents: 0 });
    }

    cartonFor(metrics = this.metrics()) {
      if (metrics.itemCount > this.config.maxItems || metrics.weightGrams > this.config.maxWeightGrams) return null;
      return metrics.itemCount > this.config.mediumItems || metrics.weightGrams > this.config.mediumWeightGrams ? "L" : "M";
    }

    canIncrement(item) {
      const candidate = this.items.map((entry) => entry === item ? { ...entry, quantity: entry.quantity + 1 } : entry);
      return Boolean(this.cartonFor(this.metrics(candidate)));
    }

    selectVariant(select) {
      const product = select.closest("[data-hamper-product]");
      if (!product) return;
      this.items = this.items.filter((entry) => entry.product !== product);
      if (select.value) {
        const option = select.selectedOptions?.[0];
        if (!option || option.disabled) {
          select.value = "";
        } else {
          const item = this.itemFromOption(product, option);
          const candidate = [...this.items, item];
          if (!this.cartonFor(this.metrics(candidate))) {
            select.value = "";
            this.notice(`${item.name || "That product"} would exceed the ${this.config.maxItems}-item or ${this.config.maxWeightGrams / 1000} kg hamper limit.`, "error");
          } else {
            this.items = candidate;
            this.notice("");
          }
        }
      } else {
        this.notice("");
      }
      this.render();
    }

    adjustQuantity(button) {
      if (this.adding) return;
      const product = button.closest("[data-hamper-product]");
      const item = this.items.find((entry) => entry.product === product);
      if (!item) return;
      if (button.matches("[data-hamper-plus]")) {
        if (!this.canIncrement(item)) {
          this.notice(`Another ${item.name || "item"} would exceed the ${this.config.maxItems}-item or ${this.config.maxWeightGrams / 1000} kg hamper limit.`, "error");
          return;
        }
        item.quantity += 1;
      } else {
        item.quantity -= 1;
        if (item.quantity <= 0) {
          product.querySelector("[data-hamper-size]").value = "";
          this.items = this.items.filter((entry) => entry !== item);
        }
      }
      this.notice("");
      this.render();
    }

    selectedVoucher() {
      const value = clean(this.query("[data-hamper-voucher]")?.value, 20);
      const variant = this.config.vouchers[value];
      return value && variant?.available ? { value, ...variant } : null;
    }

    syncVoucherAvailability() {
      const select = this.query("[data-hamper-voucher]");
      if (!select) return;
      let anyAvailable = false;
      [...select.options].forEach((option) => {
        if (!option.value) return;
        const available = this.config.vouchers[option.value]?.available === true;
        option.disabled = !available;
        anyAvailable ||= available;
      });
      if (select.value && !this.config.vouchers[select.value]?.available) select.value = "";
      select.disabled = this.adding || !anyAvailable;
    }

    syncWrapping(carton) {
      const checkbox = this.query("[data-hamper-wrap]");
      const price = this.query("[data-hamper-wrap-price]");
      if (!checkbox) return null;
      const label = carton === "L" ? "Large" : "Medium";
      const variant = this.config.wrapping[carton || "M"];
      if (price) price.textContent = variant?.available
        ? `${moneyAmount(variant.priceCents)} ${label} excl. VAT`
        : `Unavailable for ${label}`;
      if (!variant?.available && checkbox.checked) {
        checkbox.checked = false;
        if (!this.query("[data-hamper-notice]")?.textContent) this.notice(`Gift wrapping is unavailable for the ${label} carton and was removed.`, "error");
      }
      checkbox.disabled = this.adding || !variant?.available;
      return checkbox.checked && variant?.available ? variant : null;
    }

    totals(metrics, carton, wrapping, voucher) {
      const packaging = metrics.itemCount && carton ? this.config.product[carton] : null;
      return calculateTotals(metrics, packaging, wrapping, voucher);
    }

    setDateValidity() {
      const input = this.query("[data-hamper-date]");
      if (!input) return true;
      const valid = requestedDateIsValid(input.value);
      input.setAttribute("aria-invalid", String(!valid));
      input.setCustomValidity?.(valid ? "" : "Choose today or a future needed-by date.");
      return valid;
    }

    validation(metrics = this.metrics(), carton = this.cartonFor(metrics)) {
      const dateValid = this.setDateValidity();
      if (metrics.itemCount > this.config.maxItems || metrics.weightGrams > this.config.maxWeightGrams || !carton) {
        return { ready: false, message: `This selection exceeds the ${this.config.maxItems}-item or ${this.config.maxWeightGrams / 1000} kg hamper limit.` };
      }
      if (metrics.itemCount < this.config.minItems) {
        const remaining = this.config.minItems - metrics.itemCount;
        return { ready: false, message: `Add ${remaining} more item${remaining === 1 ? "" : "s"} to continue.` };
      }
      if (metrics.merchandiseCents < this.config.minMerchandiseCents) {
        return { ready: false, message: `Add ${moneyAmount(this.config.minMerchandiseCents - metrics.merchandiseCents)} more in food products.` };
      }
      const packaging = this.config.product[carton];
      if (!packaging?.available) {
        return { ready: false, message: `The ${carton === "L" ? "Large" : "Medium"} hamper carton is currently unavailable.` };
      }
      if (!dateValid) {
        return { ready: false, message: "Choose today or a future needed-by date." };
      }
      return { ready: true, message: `${metrics.itemCount} item${metrics.itemCount === 1 ? "" : "s"} in a ${carton === "L" ? "Large" : "Medium"} carton.` };
    }

    renderPreview(metrics, carton) {
      const preview = this.query("[data-hamper-preview-items]");
      if (preview) {
        preview.innerHTML = this.items.length
          ? this.items.flatMap((entry) => Array.from({ length: entry.quantity }, () => `<div class="fl-hamper-builder__preview-item">${entry.image ? `<img src="${escapeHtml(entry.image, 1000)}" alt="">` : ""}<small>${escapeHtml(entry.name, 32)}</small></div>`)).join("")
          : '<p class="fl-hamper-builder__empty-preview">Your selected Lekka goodies will appear here.</p>';
      }
      const cartonNode = this.query("[data-hamper-carton]");
      cartonNode?.setAttribute("data-hamper-carton", carton || "OVER");
      const capacity = this.query("[data-hamper-capacity]");
      if (capacity) capacity.textContent = `${carton ? `${carton === "L" ? "Large" : "Medium"} carton` : "Over capacity"} · ${metrics.itemCount} item${metrics.itemCount === 1 ? "" : "s"} · ${metrics.weightGrams} g used`;
      const percentage = Math.min(100, Math.max(
        metrics.itemCount / this.config.maxItems * 100,
        metrics.weightGrams / this.config.maxWeightGrams * 100
      ));
      const meter = this.query("[data-hamper-meter]");
      if (meter) meter.style.width = `${percentage}%`;
      const progress = this.query("[data-hamper-progress]");
      if (progress) {
        progress.setAttribute("aria-valuenow", String(Math.round(percentage)));
        progress.setAttribute("aria-valuetext", capacity?.textContent || `${Math.round(percentage)}% full`);
      }
    }

    renderQuantityControls() {
      this.products.forEach((product) => {
        const item = this.items.find((entry) => entry.product === product);
        const quantity = product.querySelector("[data-hamper-qty]");
        const output = product.querySelector("[data-hamper-quantity]");
        const minus = product.querySelector("[data-hamper-minus]");
        const plus = product.querySelector("[data-hamper-plus]");
        const select = product.querySelector("[data-hamper-size]");
        if (quantity) quantity.hidden = !item;
        if (output) output.textContent = String(item?.quantity || 0);
        if (minus) minus.disabled = this.adding || !item;
        if (plus) {
          plus.disabled = this.adding || !item || !this.canIncrement(item);
          plus.title = item && !this.canIncrement(item) ? "This item would exceed hamper capacity" : "";
        }
        if (select) select.disabled = this.adding;
      });
    }

    renderBreakdown(totals, metrics, carton, wrapping, voucher) {
      const total = this.query("[data-hamper-total]");
      if (total) total.textContent = money(totals.totalCents);
      const breakdown = this.query("[data-hamper-breakdown]");
      if (!breakdown) return;
      if (!metrics.itemCount) {
        breakdown.textContent = voucher ? `Voucher ${moneyAmount(totals.voucherCents)} selected; add products to build the hamper.` : "Choose products to see the total.";
        return;
      }
      const parts = [
        `Products ${moneyAmount(totals.merchandiseCents)}`,
        carton ? `${carton === "L" ? "Large" : "Medium"} carton ${moneyAmount(totals.packagingCents)}` : "Carton unavailable"
      ];
      if (wrapping) parts.push(`Gift wrap ${moneyAmount(totals.wrappingCents)}`);
      if (voucher) parts.push(`Voucher ${moneyAmount(totals.voucherCents)}`);
      breakdown.textContent = parts.join(" + ");
    }

    render({ persist = true } = {}) {
      this.syncVoucherAvailability();
      const metrics = this.metrics();
      const carton = this.cartonFor(metrics);
      const wrapping = this.syncWrapping(carton || "M");
      const voucher = this.selectedVoucher();
      const totals = this.totals(metrics, carton, wrapping, voucher);
      const validation = this.validation(metrics, carton);

      this.renderPreview(metrics, carton);
      this.renderQuantityControls();
      this.renderBreakdown(totals, metrics, carton, wrapping, voucher);
      const status = this.query("[data-hamper-status]");
      if (status) status.textContent = validation.message;
      const add = this.query("[data-hamper-add]");
      if (add) {
        add.disabled = this.adding || !validation.ready;
        add.textContent = this.adding ? "Adding hamper…" : "Add hamper to cart";
      }
      const generate = this.query("[data-hamper-generate]");
      if (generate) {
        generate.disabled = this.generating || this.adding;
        generate.textContent = this.generating ? "Generating…" : "✨ Generate message ideas";
      }
      const suggestions = this.query("[data-hamper-suggestions]");
      if (suggestions) {
        suggestions.hidden = !this.suggestions.length;
        suggestions.innerHTML = this.suggestions.map((suggestion) => `<button type="button" data-hamper-suggestion>${escapeHtml(suggestion, this.config.messageLimit)}</button>`).join("");
      }
      if (persist) this.saveDraft();
    }

    notice(message, tone = "") {
      const node = this.query("[data-hamper-notice]");
      if (!node) return;
      node.textContent = clean(message, 500);
      if (tone) node.dataset.tone = tone;
      else delete node.dataset.tone;
    }

    draftFields() {
      return {
        recipient: clean(this.query("[data-hamper-recipient]")?.value, 80),
        sender: clean(this.query("[data-hamper-sender]")?.value, 80),
        message: clean(this.query("[data-hamper-message]")?.value, this.config.messageLimit),
        neededBy: clean(this.query("[data-hamper-date]")?.value, 10),
        wrapping: Boolean(this.query("[data-hamper-wrap]")?.checked),
        voucher: clean(this.query("[data-hamper-voucher]")?.value, 20)
      };
    }

    saveDraft() {
      try {
        globalThis.localStorage?.setItem(this.storageKey, JSON.stringify({
          version: DRAFT_VERSION,
          savedAt: new Date().toISOString(),
          items: this.items.map((entry) => ({ variantId: entry.variantId, quantity: entry.quantity })),
          ...this.draftFields()
        }));
      } catch {
        // Storage can be unavailable in private browsing; the builder remains usable.
      }
    }

    clearDraft() {
      try {
        globalThis.localStorage?.removeItem(this.storageKey);
      } catch {
        // Ignore browser storage failures.
      }
    }

    restoreDraft() {
      let draft;
      try {
        draft = JSON.parse(globalThis.localStorage?.getItem(this.storageKey) || "null");
      } catch {
        return;
      }
      if (!draft || draft.version !== DRAFT_VERSION) return;
      for (const saved of Array.isArray(draft.items) ? draft.items : []) {
        const variantId = clean(saved?.variantId, 40);
        let restored = false;
        for (const product of this.products) {
          const select = product.querySelector("[data-hamper-size]");
          const option = [...(select?.options || [])].find((entry) => entry.value === variantId && !entry.disabled);
          if (!option) continue;
          const item = this.itemFromOption(product, option, Math.min(this.config.maxItems, positiveInteger(saved?.quantity)));
          const candidate = [...this.items, item];
          if (!this.cartonFor(this.metrics(candidate))) break;
          select.value = variantId;
          this.items = candidate;
          restored = true;
          break;
        }
        if (!restored) continue;
      }
      const assign = (selector, value, limit) => {
        const input = this.query(selector);
        if (input) input.value = clean(value, limit);
      };
      assign("[data-hamper-recipient]", draft.recipient, 80);
      assign("[data-hamper-sender]", draft.sender, 80);
      assign("[data-hamper-message]", draft.message, this.config.messageLimit);
      if (requestedDateIsValid(draft.neededBy)) assign("[data-hamper-date]", draft.neededBy, 10);
      const wrap = this.query("[data-hamper-wrap]");
      if (wrap) wrap.checked = draft.wrapping === true;
      const voucher = this.query("[data-hamper-voucher]");
      if (voucher && this.config.vouchers[clean(draft.voucher, 20)]?.available) voucher.value = clean(draft.voucher, 20);
    }

    hasUserState() {
      const fields = this.draftFields();
      return Boolean(this.items.length || fields.recipient || fields.sender || fields.message || fields.neededBy || fields.wrapping || fields.voucher);
    }

    startOver() {
      if (this.hasUserState() && !globalThis.confirm("Start over and clear all selected products, add-ons, delivery details and gift-message text?")) return;
      this.items = [];
      this.suggestions = [];
      this.products.forEach((product) => {
        const select = product.querySelector("[data-hamper-size]");
        if (select) select.value = "";
      });
      for (const selector of ["[data-hamper-recipient]", "[data-hamper-sender]", "[data-hamper-message]", "[data-hamper-date]"]) {
        const field = this.query(selector);
        if (field) field.value = "";
      }
      const wrap = this.query("[data-hamper-wrap]");
      if (wrap) wrap.checked = false;
      const voucher = this.query("[data-hamper-voucher]");
      if (voucher) voucher.value = "";
      this.clearDraft();
      this.notice("Your hamper builder was cleared.", "success");
      this.render({ persist: false });
    }

    async generateMessages() {
      if (this.generating || this.adding) return;
      const recipient = clean(this.query("[data-hamper-recipient]")?.value, 80);
      const sender = clean(this.query("[data-hamper-sender]")?.value, 80);
      const currentDraft = clean(this.query("[data-hamper-message]")?.value, this.config.messageLimit);
      if (!recipient && !sender && !currentDraft && !this.items.length) {
        this.notice("Add a recipient, sender, draft message or hamper product before generating ideas.", "error");
        return;
      }
      if (!this.config.messageEndpoint) {
        this.notice("Message ideas are unavailable, but you can still type your own message.", "error");
        return;
      }
      this.generating = true;
      this.notice("");
      this.render();
      try {
        const response = await fetch(this.config.messageEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ recipient, sender, currentDraft, flavours: this.items.map((entry) => entry.name) })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok || !Array.isArray(payload.suggestions)) throw new Error(payload.message || "Message generation failed.");
        this.suggestions = payload.suggestions.map((suggestion) => clean(suggestion, this.config.messageLimit)).filter(Boolean).slice(0, 3);
        if (!this.suggestions.length) throw new Error("No message ideas were returned.");
        this.notice("Choose an idea below or keep typing your own.", "success");
      } catch (error) {
        this.suggestions = [];
        this.notice(error?.message || "Message ideas are unavailable. You can still type your own.", "error");
      } finally {
        this.generating = false;
        this.render();
      }
    }

    packagingProperties({ id, groupId, manifest, carton, wrapping, voucher, date }) {
      const fields = this.draftFields();
      const visibleContents = manifest.map((entry) => `${entry.quantity} × ${entry.name}`).join(", ").slice(0, 500);
      return {
        "Gift hamper": `Hamper 1 of 1 · ${carton === "L" ? "Large" : "Medium"}`,
        Carton: carton === "L" ? "Large" : "Medium",
        Contents: visibleContents,
        ...(fields.recipient ? { "Gift recipient": fields.recipient } : {}),
        ...(fields.sender ? { "Gift sender": fields.sender } : {}),
        ...(fields.message ? { "Gift message": fields.message } : {}),
        ...(date ? { "Needed by (request only)": date } : {}),
        _flss_gift_hamper_id: id,
        _flss_gift_hamper_group_id: groupId,
        _flss_gift_hamper_schema: HAMPER_SCHEMA,
        _flss_gift_hamper_role: "packaging",
        _flss_gift_hamper_carton: carton,
        _flss_gift_hamper_sequence: "1",
        _flss_gift_hamper_count: "1",
        _flss_gift_hamper_manifest: JSON.stringify(manifest),
        _flss_gift_hamper_wrapping: wrapping ? carton : "none",
        _flss_gift_hamper_voucher: voucher?.value || "none",
        ...(wrapping?.variantId ? { _flss_gift_hamper_wrapping_variant_id: clean(wrapping.variantId, 40) } : {}),
        ...(voucher?.variantId ? { _flss_gift_hamper_voucher_variant_id: clean(voucher.variantId, 40) } : {}),
        ...(date ? { _flss_preferred_delivery_date: date } : {})
      };
    }

    cartItems({ carton, wrapping, voucher, date }) {
      const packaging = this.config.product[carton];
      const parentId = ajaxId(packaging.variantId);
      const groupId = `hamper-${uid()}`;
      const id = `${groupId}-1`;
      const manifest = compactManifest(this.items);
      const items = [{
        id: parentId,
        quantity: 1,
        properties: this.packagingProperties({ id, groupId, manifest, carton, wrapping, voucher, date })
      }];
      for (const entry of this.items) {
        items.push({
          id: ajaxId(entry.variantId),
          parent_id: parentId,
          quantity: entry.quantity,
          properties: privateChildProperties({ id, groupId, role: "product", carton })
        });
      }
      if (wrapping) {
        items.push({
          id: ajaxId(wrapping.variantId),
          parent_id: parentId,
          quantity: 1,
          properties: privateChildProperties({ id, groupId, role: "wrapping", carton })
        });
      }
      if (voucher) {
        items.push({
          id: ajaxId(voucher.variantId),
          parent_id: parentId,
          quantity: 1,
          properties: privateChildProperties({ id, groupId, role: "voucher", carton })
        });
      }
      return items;
    }

    async addToCart() {
      if (this.adding) return;
      const metrics = this.metrics();
      const carton = this.cartonFor(metrics);
      const validation = this.validation(metrics, carton);
      if (!validation.ready) {
        this.notice(validation.message, "error");
        return;
      }
      const wrappingCheckbox = this.query("[data-hamper-wrap]");
      const wrapping = wrappingCheckbox?.checked ? this.config.wrapping[carton] : null;
      if (wrappingCheckbox?.checked && !wrapping?.available) {
        this.notice(`Gift wrapping is unavailable for the ${carton === "L" ? "Large" : "Medium"} carton.`, "error");
        return;
      }
      const voucherValue = clean(this.query("[data-hamper-voucher]")?.value, 20);
      const voucher = this.selectedVoucher();
      if (voucherValue && !voucher) {
        this.notice("That voucher is not available yet. Choose no voucher to continue.", "error");
        return;
      }
      const date = validIsoDate(this.query("[data-hamper-date]")?.value);
      if (!this.config.cartAddUrl) {
        this.notice("The hamper cart connection is unavailable. Please try again later.", "error");
        return;
      }

      this.adding = true;
      this.notice("");
      this.render();
      let succeeded = false;
      try {
        const response = await fetch(this.config.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: this.cartItems({ carton, wrapping, voucher, date }) })
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.description || payload.message || "The hamper could not be added to the cart.");
        succeeded = true;
        this.clearDraft();
        globalThis.location.assign(this.config.cartUrl);
      } catch (error) {
        this.notice(error?.message || "The hamper could not be added. Check your connection and try again.", "error");
      } finally {
        this.adding = false;
        this.render({ persist: !succeeded });
      }
    }
  }

  if (globalThis.__FL_GIFT_HAMPER_TEST_HOOK__ && typeof globalThis.__FL_GIFT_HAMPER_TEST_HOOK__ === "object") {
    Object.assign(globalThis.__FL_GIFT_HAMPER_TEST_HOOK__, {
      calculateTotals,
      compactManifest,
      normalizeConfig,
      privateChildProperties,
      requestedDateIsValid,
      validIsoDate
    });
  }

  function initialize(scope = document) {
    const roots = [];
    if (scope instanceof Element && scope.matches(BUILDER_SELECTOR)) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.(BUILDER_SELECTOR) || []));
    for (const root of [...new Set(roots)]) {
      if (root.hasAttribute("data-fl-hamper-initialized")) continue;
      root.setAttribute("data-fl-hamper-initialized", "true");
      try {
        new HamperBuilder(root);
      } catch (error) {
        const notice = root.querySelector("[data-hamper-notice]");
        if (notice) {
          notice.setAttribute("role", "alert");
          notice.textContent = error?.message || "The gift hamper builder could not be loaded.";
        }
      }
    }
  }

  initialize();
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
